import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useFormik} from 'formik'
import {register} from '../core/_requests'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import PreventAutoComplete from '../../../components/PreventAutoComplete'
import clsx from 'clsx'
import SatkerSelect from './SatkerSelect'
import OfficerRankSelect from './OfficerRankSelect'
import {InputGroup} from 'react-bootstrap'
import LoadingButtonWrapper from '../../../components/LoadingButtonWrapper'
import {Link} from 'react-router-dom'
import {GoogleReCaptcha, GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {intervalRefreshRecaptcha} from '../../../../_metronic/helpers'
import {scrollToFieldError} from '../../../utils/view'
import {useAppIntl} from '../../../i18n'
import {createRegistrationSchema} from '../authSchemas'

const initialValues: any = {
  name: '',
  email: '',
  password: '',
  changepassword: '',
  nrp: '',
  nik: '',
  satkerId: undefined,
  officerRankId: undefined,
}

const isUseCaptcha = process.env.REACT_APP_USE_CAPTCHA === 'true'
const captchaKey = process.env.REACT_APP_CAPTCHA_TOKEN ?? ''

export function RegistrationForm() {
  const {formatMessage} = useAppIntl()
  const validationSchema = useMemo(
    () => createRegistrationSchema(formatMessage),
    [formatMessage]
  )
  const [loading, setLoading] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string>('')
  const [refreshCaptcha, setRefreshCaptcha] = useState<boolean>(false)
  const errorRef = useRef<HTMLDivElement|null>(null)

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setStatus(null)
      setLoading(true)
      try {
        let request = {captchaToken: '', ...values}
        if (isUseCaptcha) {
          request = {captchaToken, ...values}
        }

        await register(request)
        setShowInfo(true)
        setSubmitting(false)
        setLoading(false)
      } catch (error: any) {
        console.error(error.message)
        setStatus(error.message)
        setSubmitting(false)
        setLoading(false)
        setRefreshCaptcha(!refreshCaptcha)
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  })

  const onVerify = useCallback((token) => {
    setCaptchaToken(token)
  }, [])

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
    const intervalId = setInterval(() => {
      setRefreshCaptcha((r) => !r)
    }, intervalRefreshRecaptcha)


    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible)
  }

  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
  }

  return (
    <>
      {!showInfo && (
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          id='kt_login_signup_form'
          onSubmit={formik.handleSubmit}
        >
          <PreventAutoComplete />
          {/* begin::Heading */}

          <div ref={errorRef}>
            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}
          </div>

          {/* begin::Form group Name */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.fullName'})}
            </label>
            <input
              placeholder={formatMessage({id: 'auth.register.fullName.placeholder'})}
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('name')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.name && formik.errors.name,
                },
                {
                  'is-valid': formik.touched.name && !formik.errors.name,
                }
              )}
            />
            {formik.touched.name && formik.errors.name && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.name + ''}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}
          <div className='fv-row mb-8'>
            {/* begin::Form group NIK */}
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.nik'})}
            </label>
            <input
              placeholder={formatMessage({id: 'auth.register.nik.placeholder'})}
              type='text'
              autoComplete='off'
              maxLength={16}
              {...formik.getFieldProps('nik')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.nik && formik.errors.nik,
                },
                {
                  'is-valid': formik.touched.nik && !formik.errors.nik,
                }
              )}
            />
            {formik.touched.nik && formik.errors.nik && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.nik + ''}</span>
                </div>
              </div>
            )}
            {/* end::Form group */}
          </div>

          <div className='fv-row mb-8'>
            {/* begin::Form group NRP */}
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.nrp'})}
            </label>
            <input
              placeholder={formatMessage({id: 'auth.register.nrp.placeholder'})}
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('nrp')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.nrp && formik.errors.nrp,
                },
                {
                  'is-valid': formik.touched.nrp && !formik.errors.nrp,
                }
              )}
            />
            {formik.touched.nrp && formik.errors.nrp && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.nrp + ''}</span>
                </div>
              </div>
            )}
            {/* end::Form group */}
          </div>

          <div className='fv-row mb-8'>
            {/* begin::Form group Satker */}
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.satker'})}
            </label>
            <SatkerSelect
              field={formik.getFieldProps('satkerId')}
              form={formik}
              className={clsx(
                {
                  'is-invalid': formik.touched.satkerId && formik.errors.satkerId,
                },
                {
                  'is-valid': formik.touched.satkerId && !formik.errors.satkerId,
                }
              )}
            />
            {formik.touched.satkerId && formik.errors.satkerId && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.satkerId + ''}</span>
                </div>
              </div>
            )}
            {/* end::Form group */}
          </div>

          <div className='fv-row mb-8'>
            {/* begin::Form group Officer Rank */}
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.officerRank'})}
            </label>
            <OfficerRankSelect
              field={formik.getFieldProps('officerRankId')}
              form={formik}
              className={clsx(
                {
                  'is-invalid': formik.touched.officerRankId && formik.errors.officerRankId,
                },
                {
                  'is-valid': formik.touched.officerRankId && !formik.errors.officerRankId,
                }
              )}
            />
            {formik.touched.officerRankId && formik.errors.officerRankId && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.officerRankId + ''}</span>
                </div>
              </div>
            )}
            {/* end::Form group */}
          </div>

          {/* begin::Form group Email */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.email'})}
            </label>
            <input
              placeholder={formatMessage({id: 'auth.register.email.placeholder'})}
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                }
              )}
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.email + ''}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group Password */}
          <div className='fv-row mb-8' data-kt-password-meter='true'>
            <div className='mb-1'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                {formatMessage({id: 'auth.register.password'})}
              </label>
              <div className='position-relative mb-3'>
                <InputGroup>
                  <input
                    type={isPasswordVisible ? 'text' : 'password'}
                    placeholder={formatMessage({id: 'auth.register.password.placeholder'})}
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid': formik.touched.password && formik.errors.password,
                      },
                      {
                        'is-valid': formik.touched.password && !formik.errors.password,
                      }
                    )}
                  />
                  <InputGroup.Text role='button' onClick={togglePasswordVisibility}>
                    <i className={!isPasswordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                  </InputGroup.Text>
                </InputGroup>
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.password + ''}</span>
                    </div>
                  </div>
                )}
              </div>
              {/* begin::Meter */}
              <div
                className='d-flex align-items-center mb-3'
                data-kt-password-meter-control='highlight'
              >
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
                <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
              </div>
              {/* end::Meter */}
            </div>
            <div className='text-muted'>{formatMessage({id: 'auth.register.passwordHint'})}</div>
          </div>
          {/* end::Form group */}

          {/* begin::Form group Confirm password */}
          <div className='fv-row mb-5'>
            <label className='form-label fw-bolder text-dark fs-6 required'>
              {formatMessage({id: 'auth.register.confirmPassword'})}
            </label>
            <InputGroup>
              <input
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                placeholder={formatMessage({id: 'auth.register.confirmPassword.placeholder'})}
                autoComplete='off'
                {...formik.getFieldProps('changepassword')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
                  },
                  {
                    'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
                  }
                )}
              />
              <InputGroup.Text role='button' onClick={toggleConfirmPasswordVisibility}>
                <i className={!isConfirmPasswordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </InputGroup.Text>
            </InputGroup>
            {formik.touched.changepassword && formik.errors.changepassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.changepassword + ''}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='text-center'>
            <LoadingButtonWrapper isLoading={loading}>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='btn btn-lg btn-danger w-100 mb-5'
                disabled={loading && formik.isValid}
              >
                <span className='indicator-label'>
                  {formatMessage({id: 'common.action.submit'})}
                </span>
              </button>
            </LoadingButtonWrapper>
            <Link to='/auth/login'>
              <button
                type='button'
                id='kt_login_signup_form_cancel_button'
                className='btn btn-lg btn-light-danger w-100 mb-5'
              >
                {formatMessage({id: 'common.action.cancel'})}
              </button>
            </Link>
          </div>
          {/* end::Form group */}
          {isUseCaptcha && (
            <GoogleReCaptchaProvider reCaptchaKey={captchaKey}>
              <GoogleReCaptcha
                onVerify={onVerify}
                refreshReCaptcha={refreshCaptcha}
                action='register'
              />
            </GoogleReCaptchaProvider>
          )}
        </form>
      )}

      {showInfo && (
        <div className='text-center'>
          <h3>{formatMessage({id: 'auth.register.successMessage'})}</h3>

          <Link to='/auth/login'>
            <button
              type='button'
              id='kt_login_signup_form_cancel_button'
              className='btn btn-lg btn-danger w-100 mb-5 mt-14'
            >
              {formatMessage({id: 'auth.register.backToLogin'})}
            </button>
          </Link>
        </div>
      )}
    </>
  )
}
